import {
  Box,
  Button,
  Card,
  Checkbox, Divider,
  Drawer,
  FormControlLabel,
  IconButton, Stack, Typography
} from '@mui/material';

import React, { useContext, useEffect, useRef, useState } from 'react';
import { Periodic, TagSelector } from '../../../features';
import { X } from '@phosphor-icons/react';
import { PeriodDateRange } from '../../../features/period-date-range';
import { SlaFilterInput } from '@coverright/data-access/types/enrollment';

type Filters = Periodic<SlaFilterInput, 'range'>

interface TwoHoursSlaFilterDrawerProps {
  open: boolean,
  onClose: (filter?: Filters) => void,
  initial: Filters
}

interface Props {
  open: (onCloseCallback: (filter: Filters) => void, filters: Filters) => void;
}

const TwoHoursSlaFilterDrawerContext = React.createContext<Props>({
  open: () => {}
});

export function withTwoHoursSlaFilterDrawer(WrappedComponent: React.ComponentType) {
  return () => {
    const [open, setOpen] = useState(false);
    const [filters, setFilters] = useState<Filters>({});
    const callback = useRef<(filter: Filters) => void>();

    const onClose: TwoHoursSlaFilterDrawerProps['onClose'] = filter => {
      setOpen(false);
      filter && callback.current && callback.current(filter);
    };

    const openDrawer = (onCloseCallback: (filter: Filters) => void, filter: Filters) => {
      setFilters(filter);
      callback.current = onCloseCallback;
      setOpen(true);
    };

    return <TwoHoursSlaFilterDrawerContext.Provider value={{ open: openDrawer }}>
      <TwoHoursSlaFilterDrawer open={open} onClose={onClose} initial={filters} />
      <WrappedComponent />
    </TwoHoursSlaFilterDrawerContext.Provider>;
  };
}

export const useTwoHoursSlaFilterDrawer = () => useContext(TwoHoursSlaFilterDrawerContext);

function TwoHoursSlaFilterDrawer({ open, onClose, initial }: TwoHoursSlaFilterDrawerProps) {
  const [filters, setFilters] = useState(initial || {});

  useEffect(() => {
    if (open) {
      setFilters(initial || {});
    } else {
      setFilters({});
    }
  }, [open, initial]);

  const setFilterValues = (values: Array<{ name: keyof SlaFilterInput, value: any }>) => {
    setFilters(prev => {
      const result = prev ? { ...prev } : {};
      for (const value of values) {
        result[value.name] = value.value === '-' ? undefined : value.value || undefined;
      }
      return result;
    });
  };

  return <Drawer
    anchor={'right'}
    open={open}
    onClose={() => onClose()}
  >
    <Card sx={{ p: 4, width: 500, overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant={'h3'}>2 Hour SLA filters</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton size={'small'} onClick={() => onClose()}>
            <X />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ mb: 3, mt: 1 }} />
      <Stack component={'form'} onSubmit={(e: any) => e.preventDefault()} spacing={3}>
        <PeriodDateRange label={'Date'} value={filters.range || {}} onChange={(value) => {
          setFilterValues([{ name: 'range', value }])
        }} />
        <TagSelector selectedValues={filters.tags || []} label={'Tags'}
                     onChange={value => setFilterValues([{ name: 'tags', value: value.map(t => t.id) }])} />
        <FormControlLabel
          control={
            <Checkbox
              checked={!!filters.excludeBadNumber}
              onChange={(e, checked) => setFilterValues([{ name: 'excludeBadNumber', value: checked }])}
              color="primary"
            />
          }
          label="Exclude Bad Numbers"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={!!filters.excludeNotAttempted}
              onChange={(e, checked) => setFilterValues([{ name: 'excludeNotAttempted', value: checked }])}
              color="primary"
            />
          }
          label="Exclude Not Attempted"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={!!filters.excludeDoNotCall}
              onChange={(e, checked) => setFilterValues([{ name: 'excludeDoNotCall', value: checked }])}
              color="primary"
            />
          }
          label="Exclude Do Not Call"
        />

        <div>
          <Button variant={'contained'} type={'submit'} onClick={() => onClose(filters)}>Search</Button>
        </div>
      </Stack>
    </Card>
  </Drawer>;
}
